import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { niceTicks, refLabelPositions, useMeasuredWidth } from '../charts';
import {
    SOURCE_META, clipToDomain, deltaVsAsk, fmtDay, fmtMonth, fmtNum, fmtUsd, fmtUsdShort, meterShort, monthTicks, placeOf,
    robustDomain, timeDomain,
} from '../../../utils/compsSources';

// "Price over time" on the Comps tab: one dot per record, shape + colour per
// source (colour-blind safe: the shape alone tells them apart), the ask and
// the IronSight estimate as labelled lines. Colours are CSS classes on
// --v2-* tokens (CompsSourcesV2.css).
//
// Interaction: hover / tap picks the nearest point within HIT_PX (a target
// far larger than the 9px mark); the chart is one focusable element whose
// ←/→ (Home / End) walk the points in time order, Escape clears. The read-out
// is announced politely. Selecting a point tells the parent (onSelect), which
// highlights the matching record card.

const HEIGHT = 300;
const HIT_PX = 24;
const MARK = 4.5;

export function Marker({
    shape, x, y, r = MARK, className,
}) {
    const cls = `cs2-mk cs2-mk--${shape}${className ? ` ${className}` : ''}`;
    if (shape === 'diamond') {
        const d = r * 1.35;
        return <path className={cls} d={`M${x} ${y - d}L${x + d} ${y}L${x} ${y + d}L${x - d} ${y}Z`} />;
    }
    if (shape === 'square') return <rect className={cls} x={x - r} y={y - r} width={r * 2} height={r * 2} rx={1} />;
    return <circle className={cls} cx={x} cy={y} r={shape === 'ring' ? r - 0.5 : r} />;
}
Marker.propTypes = {
    shape: PropTypes.string.isRequired, x: PropTypes.number.isRequired, y: PropTypes.number.isRequired, r: PropTypes.number, className: PropTypes.string,
};

/** A shape swatch for legends and cards. */
export function Swatch({ source }) {
    return (
        <svg className="cs2-sw" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
            <Marker shape={SOURCE_META[source].shape} x={7} y={7} r={4.5} />
        </svg>
    );
}
Swatch.propTypes = { source: PropTypes.string.isRequired };

/** The read-out of one record: date · price · year · hours · place · source · vs ask. */
export function readoutParts(r, { ask, meter }) {
    const meta = SOURCE_META[r.source];
    let hours = null;
    if (r.hours != null) hours = `${fmtNum(r.hours)} ${r.source === 'own' ? 'engine h' : meterShort(r.meterUnit || meter)}`;
    return [
        fmtDay(r.t),
        `${fmtUsd(r.price)}${r.source === 'advertised' ? ' asking' : ''}`,
        r.year != null ? String(r.year) : null,
        hours,
        placeOf(r),
        meta.label,
        deltaVsAsk(r.price, ask)?.text || null,
    ].filter(Boolean);
}

export default function CompsScatter({
    series, ask, estimate, range, now, selectedId, onSelect, label, meter,
}) {
    const [wrapRef, width] = useMeasuredWidth();
    const [hoverId, setHoverId] = useState(null);
    const svgRef = useRef(null);
    const narrow = width < 520;
    const pad = {
        l: 50, r: narrow ? 64 : 112, t: 22, b: 26,
    };
    const iw = Math.max(40, width - pad.l - pad.r);
    const ih = HEIGHT - pad.t - pad.b;

    const all = useMemo(() => series.flatMap((s) => s.records), [series]);
    const geo = useMemo(() => {
        // Nothing priced to plot: no chart (the ask and estimate alone are not one).
        if (!all.some((r) => r.price != null && r.t != null)) return null;
        const dom = robustDomain(all.map((r) => r.price), [ask, estimate]);
        if (!dom) return null;
        const yt = niceTicks(dom[0], dom[1], 4);
        const lo = Math.max(dom[0], yt.lo);
        const hi = Math.min(dom[1], yt.hi);
        const [t0, t1] = timeDomain(range, now, all);
        const x = (t) => pad.l + ((t - t0) / (t1 - t0 || 1)) * iw;
        const y = (v) => pad.t + (1 - (v - yt.lo) / (yt.hi - yt.lo || 1)) * ih;
        const clip = clipToDomain(all.filter((r) => r.t != null && r.t >= t0 && r.t <= t1), [lo, hi]);
        const pts = [];
        const place = (r, py, edge) => pts.push({
            r, px: x(r.t), py, edge,
        });
        clip.inside.forEach((r) => place(r, y(r.price), null));
        clip.above.forEach((r) => place(r, pad.t - 6, 'above'));
        clip.below.forEach((r) => place(r, pad.t + ih + 6, 'below'));
        pts.sort((a, b) => a.r.t - b.r.t || a.r.price - b.r.price);
        return {
            x, y, yt, t0, t1, pts, above: clip.above.length, below: clip.below.length,
            xticks: monthTicks(t0, t1, narrow ? 4 : 7),
        };
    }, [all, ask, estimate, range, now, iw, ih, pad.l, pad.t, narrow]);

    // One measured wrapper in every state: the width hook observes the
    // element it saw on mount, so the ref must never move to another node.
    if (!geo) {
        return (
            <div ref={wrapRef} className="cs2-chart-wrap">
                <div className="cs2-chart cs2-chart--empty">
                    <p className="u2-note">No prices to plot in this view.</p>
                </div>
            </div>
        );
    }

    const activeId = hoverId || selectedId;
    const active = geo.pts.find((p) => p.r.id === activeId) || null;

    const nearest = (clientX, clientY) => {
        const box = svgRef.current?.getBoundingClientRect();
        if (!box || !box.width) return null;
        const sx = ((clientX - box.left) / box.width) * width;
        const sy = ((clientY - box.top) / box.height) * HEIGHT;
        let best = null;
        let bestD = HIT_PX;
        geo.pts.forEach((p) => {
            const d = Math.hypot(p.px - sx, p.py - sy);
            if (d <= bestD) { best = p; bestD = d; }
        });
        return best;
    };

    const onKeyDown = (e) => {
        if (!geo.pts.length) return;
        const i = geo.pts.findIndex((p) => p.r.id === selectedId);
        let next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = i < 0 ? 0 : Math.min(geo.pts.length - 1, i + 1);
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = i < 0 ? geo.pts.length - 1 : Math.max(0, i - 1);
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = geo.pts.length - 1;
        else if (e.key === 'Escape' && selectedId) {
            e.stopPropagation();
            onSelect(null);
            return;
        }
        if (next == null) return;
        e.preventDefault();
        onSelect(geo.pts[next].r.id, { via: 'key' });
    };

    // Line labels at the right end, kept apart.
    const refs = [];
    if (ask != null) refs.push({ key: 'ask', y: geo.y(Math.min(Math.max(ask, geo.yt.lo), geo.yt.hi)), left: false });
    if (estimate != null) refs.push({ key: 'est', y: geo.y(Math.min(Math.max(estimate, geo.yt.lo), geo.yt.hi)), left: false });
    // Each line label is two rows (name + value): keep them a full label apart.
    const refY = refLabelPositions(refs, 28);
    const refOf = (key) => refs.find((r) => r.key === key);
    const lx = pad.l + iw + 6;

    const readout = active ? readoutParts(active.r, { ask, meter }) : null;
    const tipLeft = active ? Math.min(Math.max(active.px, 110), width - 110) : 0;
    const tipBelow = active && active.py < 90;

    return (
        <div ref={wrapRef} className="cs2-chart-wrap">
            <div
                className="cs2-chart"
                role="img"
                aria-label={label}
                tabIndex={0}
                onKeyDown={onKeyDown}
                data-testid="comps-chart"
            >
                <svg
                    ref={svgRef}
                    width={width}
                    height={HEIGHT}
                    viewBox={`0 0 ${width} ${HEIGHT}`}
                    aria-hidden="true"
                    focusable="false"
                    onPointerMove={(e) => { if (e.pointerType !== 'touch') setHoverId(nearest(e.clientX, e.clientY)?.r.id || null); }}
                    onPointerLeave={() => setHoverId(null)}
                    onClick={(e) => {
                        const p = nearest(e.clientX, e.clientY);
                        onSelect(p ? p.r.id : null, { via: 'pointer' });
                    }}
                >
                    {geo.yt.ticks.map((v) => (
                        <g key={`y${v}`}>
                            <line className="cs2-grid" x1={pad.l} x2={pad.l + iw} y1={geo.y(v)} y2={geo.y(v)} />
                            <text className="cs2-axis" x={pad.l - 6} y={geo.y(v) + 4} textAnchor="end">{fmtUsdShort(v)}</text>
                        </g>
                    ))}
                    {geo.xticks.map((t) => (
                        <g key={`x${t}`}>
                            <line className="cs2-tick" x1={geo.x(t)} x2={geo.x(t)} y1={pad.t + ih} y2={pad.t + ih + 4} />
                            <text className="cs2-axis" x={geo.x(t)} y={HEIGHT - 6} textAnchor="middle">{fmtMonth(t)}</text>
                        </g>
                    ))}
                    <line className="cs2-base" x1={pad.l} x2={pad.l + iw} y1={pad.t + ih} y2={pad.t + ih} />
                    {geo.pts.map((p) => (
                        <Marker
                            key={p.r.id}
                            shape={SOURCE_META[p.r.source].shape}
                            x={p.px}
                            y={p.py}
                            className={[`cs2-mk--${p.r.source}`, p.edge && 'is-clipped'].filter(Boolean).join(' ')}
                        />
                    ))}
                    {ask != null && (
                        <g className="cs2-ref cs2-ref--ask">
                            <line x1={pad.l} x2={pad.l + iw} y1={refOf('ask').y} y2={refOf('ask').y} />
                            <text x={lx} y={refY.ask + 4}>{narrow ? 'Ask' : 'Your ask'}</text>
                            <text className="cs2-ref-v" x={lx} y={refY.ask + 16}>{fmtUsdShort(ask)}</text>
                        </g>
                    )}
                    {estimate != null && (
                        <g className="cs2-ref cs2-ref--est">
                            <line x1={pad.l} x2={pad.l + iw} y1={refOf('est').y} y2={refOf('est').y} />
                            <text x={lx} y={refY.est + 4}>{narrow ? 'Estimate' : 'IronSight estimate'}</text>
                            <text className="cs2-ref-v" x={lx} y={refY.est + 16}>{fmtUsdShort(estimate)}</text>
                        </g>
                    )}
                    {active && (
                        <g className="cs2-active">
                            <Marker
                                shape={SOURCE_META[active.r.source].shape}
                                x={active.px}
                                y={active.py}
                                r={7}
                                className={`cs2-mk--${active.r.source} is-active`}
                            />
                        </g>
                    )}
                </svg>
                {readout && (
                    <div
                        className={`cs2-tip${tipBelow ? ' is-below' : ''}`}
                        style={{ left: tipLeft, top: active.py }}
                        aria-hidden="true"
                    >
                        <b>{readout[1]}</b>
                        <span>{[readout[0], ...readout.slice(2)].join(' · ')}</span>
                    </div>
                )}
            </div>
            {(geo.above > 0 || geo.below > 0) && (
                <p className="u2-sub cs2-clipnote" data-testid="comps-clipped">
                    {[geo.above > 0 && `+${geo.above} above range`, geo.below > 0 && `+${geo.below} below range`].filter(Boolean).join(' · ')}
                    {' '}(pinned to the {geo.above > 0 && geo.below > 0 ? 'edges' : 'edge'} so the rest stay readable)
                </p>
            )}
            <p className="u2-sr-box" aria-live="polite" data-testid="comps-readout">{readout && selectedId ? readout.join(' · ') : ''}</p>
        </div>
    );
}

CompsScatter.propTypes = {
    series: PropTypes.arrayOf(PropTypes.shape({ source: PropTypes.string, records: PropTypes.array })).isRequired,
    ask: PropTypes.number,
    estimate: PropTypes.number,
    range: PropTypes.string.isRequired,
    now: PropTypes.number.isRequired,
    selectedId: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    meter: PropTypes.string,
};
